import { generateRecordId, getPointer, ThreadResolvedThreadTagRecord, ThreadVisibility } from "libs/schema";
import { toast } from "~/environment/toast-service";
import { withActionContext, write } from "./write";
import * as actions from "libs/actions";
import { InvalidActionStateError } from "libs/actions";
import { reindexNotification } from "./notification";

/* -------------------------------------------------------------------------------------------------
 * updateThreadSubject
 * -------------------------------------------------------------------------------------------------
 */

export const updateThreadSubject = withActionContext(
  async (
    context,
    environment,
    props: {
      threadId: string;
      subject: string;
    },
  ) => {
    const [originalThread] = await context.environment.loader.getRecord("thread", props.threadId);

    return {
      forward: async () => {
        toast("vanilla", {
          subject: `Updating thread subject...`,
        });

        const action = await actions.thread.updateThreadSubject.action(props, context);

        await write(environment, {
          action,
          onOptimisticWrite: () => {
            toast("vanilla", {
              subject: `Updating thread subject...Done!`,
            });

            reindexNotification(environment, { threadId: props.threadId });
          },
        });
      },
      backward: async () => {
        if (!originalThread) {
          throw new InvalidActionStateError("[updateThreadSubject] Thread not found", {
            errorData: { props },
          });
        }

        toast("vanilla", {
          subject: `Restoring thread subject...`,
        });

        const action = await actions.thread.updateThreadSubject.action(
          {
            threadId: props.threadId,
            subject: originalThread.subject,
          },
          context,
        );

        await write(environment, {
          action,
          onOptimisticWrite: () => {
            toast("vanilla", {
              subject: `Restoring thread subject...Done!`,
            });

            reindexNotification(environment, { threadId: props.threadId });
          },
        });
      },
    };
  },
);

/* -------------------------------------------------------------------------------------------------
 * addGroupToThread
 * -------------------------------------------------------------------------------------------------
 */

export const addGroupToThread = withActionContext(
  async (
    context,
    environment,
    props: {
      threadId: string;
      groupId: string;
    },
  ) => {
    return {
      forward: async () => {
        const action = await actions.thread.addGroupToThread.action(props, context);

        await write(environment, {
          action,
          onOptimisticWrite: () => {
            toast("vanilla", {
              subject: `Group added to thread.`,
            });

            reindexNotification(environment, { threadId: props.threadId });
          },
        });
      },
      backward: async () => {
        const action = await actions.thread.removeGroupFromThread.action(props, context);

        await write(environment, {
          action,
          onOptimisticWrite: () => {
            toast("vanilla", {
              subject: `Group removed from thread.`,
            });
          },
        });

        reindexNotification(environment, { threadId: props.threadId });
      },
    };
  },
);

/* -------------------------------------------------------------------------------------------------
 * removeGroupFromThread
 * -------------------------------------------------------------------------------------------------
 */

export const removeGroupFromThread = withActionContext(
  async (
    context,
    environment,
    props: {
      threadId: string;
      groupId: string;
    },
  ) => {
    return {
      forward: async () => {
        const action = await actions.thread.removeGroupFromThread.action(props, context);

        await write(environment, {
          action,
          onOptimisticWrite: () => {
            toast("vanilla", {
              subject: `Group removed from thread.`,
            });

            reindexNotification(environment, { threadId: props.threadId });
          },
        });
      },
      backward: async () => {
        const action = await actions.thread.addGroupToThread.action(props, context);

        await write(environment, {
          action,
          onOptimisticWrite: () => {
            toast("vanilla", {
              subject: `Group added to thread.`,
            });

            reindexNotification(environment, { threadId: props.threadId });
          },
        });
      },
    };
  },
);

/* -------------------------------------------------------------------------------------------------
 * applyLabelToThread
 * -------------------------------------------------------------------------------------------------
 */

export const applyLabelToThread = withActionContext(
  async (context, environment, props: { threadId: string; labelId: string }) => {
    return {
      forward: async () => {
        const action = await actions.thread.applyLabelToThread.action(props, context);

        await write(environment, {
          action,
          onOptimisticWrite: () => {
            toast("vanilla", {
              subject: `Applied label to thread.`,
            });

            reindexNotification(environment, { threadId: props.threadId });
          },
        });
      },
      backward: async () => {
        const action = await actions.thread.removeLabelFromThread.action(props, context);

        await write(environment, {
          action,
          onOptimisticWrite: () => {
            toast("vanilla", {
              subject: `Removed label from thread.`,
            });

            reindexNotification(environment, { threadId: props.threadId });
          },
        });
      },
    };
  },
);

/* -------------------------------------------------------------------------------------------------
 * removeLabelFromThread
 * -------------------------------------------------------------------------------------------------
 */

export const removeLabelFromThread = withActionContext(
  async (context, environment, props: { threadId: string; labelId: string }) => {
    return {
      forward: async () => {
        const action = await actions.thread.removeLabelFromThread.action(props, context);

        await write(environment, {
          action,
          onOptimisticWrite: () => {
            toast("vanilla", {
              subject: `Removed label from thread.`,
            });

            reindexNotification(environment, { threadId: props.threadId });
          },
        });
      },
      backward: async () => {
        const action = await actions.thread.applyLabelToThread.action(props, context);

        await write(environment, {
          action,
          onOptimisticWrite: () => {
            toast("vanilla", {
              subject: `Applied label to thread.`,
            });

            reindexNotification(environment, { threadId: props.threadId });
          },
        });
      },
    };
  },
);

/* -------------------------------------------------------------------------------------------------
 * updateThreadVisibility
 * -------------------------------------------------------------------------------------------------
 */

export const updateThreadVisibility = withActionContext(
  async (
    context,
    environment,
    props: {
      threadId: string;
      visibility: ThreadVisibility;
    },
  ) => {
    const [originalThread] = await context.environment.loader.getRecord("thread", props.threadId);

    return {
      forward: async () => {
        if (!environment.network.isOnline()) {
          throw new InvalidActionStateError("[updateThreadVisibility] [forward] Client is offline", {
            userMessage: "Not available offline",
            userHint: `
              Cannot change thread visibility while offline.
            `,
          });
        }

        const action = await actions.thread.updateThreadVisibility.action(props, context);

        await write(environment, {
          action,
          onOptimisticWrite: () => {
            toast("vanilla", {
              subject: `Making thread ${props.visibility.toLowerCase()}...`,
            });

            reindexNotification(environment, { threadId: props.threadId });
          },
        });

        toast("vanilla", {
          subject: `Making thread ${props.visibility.toLowerCase()}...Done!`,
        });
      },
      backward: async () => {
        if (!environment.network.isOnline()) {
          throw new InvalidActionStateError("[updateThreadVisibility] [backward] Client is offline", {
            userMessage: "Cannot undo",
            userHint: `
              Cannot change thread visibility while offline.
            `,
          });
        }

        if (!originalThread) {
          throw new InvalidActionStateError("[updateThreadVisibility] [backward] Thread not found", {
            errorData: { props },
          });
        }

        const action = await actions.thread.updateThreadVisibility.action(
          {
            threadId: props.threadId,
            visibility: originalThread.visibility,
          },
          context,
        );

        await write(environment, {
          action,
          onOptimisticWrite: () => {
            toast("vanilla", {
              subject: `Undoing making thread ${props.visibility.toLowerCase()}.`,
            });

            reindexNotification(environment, { threadId: props.threadId });
          },
        });

        toast("vanilla", {
          subject: `Undoing making thread ${props.visibility.toLowerCase()}...Done!`,
        });
      },
    };
  },
);

/* -------------------------------------------------------------------------------------------------
 * markThreadSeen
 * -------------------------------------------------------------------------------------------------
 */

export const markThreadSeen = withActionContext(
  async (
    context,
    environment,
    props: {
      threadId: string;
      seenToTimelineId: string;
      seenToTimelineOrder: string;
    },
  ) => {
    const ownerOrganizationId = environment.auth.getAndAssertCurrentUserOwnerOrganizationId();

    return {
      forward: async () => {
        const action = await actions.thread.markThreadSeen.action(
          {
            ...props,
            ownerOrganizationId,
          },
          context,
        );

        await write(environment, { action });
      },
    };
  },
);

/* -------------------------------------------------------------------------------------------------
 * markThreadResolved
 * -------------------------------------------------------------------------------------------------
 */

export const markThreadResolved = withActionContext(
  async (
    context,
    environment,
    props: {
      threadId: string;
      resolvedByMessageId: string;
    },
  ) => {
    const ownerOrganizationId = environment.auth.getAndAssertCurrentUserOwnerOrganizationId();

    const [_originalThreadTag] = await context.environment.loader.getRecord(
      getPointer("thread_tag", {
        thread_id: props.threadId,
        tag_id: generateRecordId("singleton_tag", { name: "resolved" }),
      }),
    );

    return {
      forward: async () => {
        const action = await actions.thread.markThreadResolved.action(
          {
            ...props,
            ownerOrganizationId,
          },
          context,
        );

        await write(environment, {
          action,
          onOptimisticWrite: () => {
            toast("vanilla", {
              subject: `Thread resolved.`,
            });

            reindexNotification(environment, { threadId: props.threadId });
          },
        });
      },
      backward: async () => {
        const originalThreadTag = _originalThreadTag as ThreadResolvedThreadTagRecord | null;

        if (originalThreadTag) {
          const action = await actions.thread.markThreadResolved.action(
            {
              threadId: props.threadId,
              resolvedByMessageId: originalThreadTag.data.message_id,
              ownerOrganizationId,
            },
            context,
          );

          await write(environment, {
            action,
            onOptimisticWrite: () => {
              toast("vanilla", {
                subject: `Restored original thread resolution.`,
              });

              reindexNotification(environment, { threadId: props.threadId });
            },
          });
        } else {
          const action = await actions.thread.markThreadNotResolved.action({ threadId: props.threadId }, context);

          await write(environment, {
            action,
            onOptimisticWrite: () => {
              toast("vanilla", {
                subject: `Marked thread as not resolved.`,
              });

              reindexNotification(environment, { threadId: props.threadId });
            },
          });
        }
      },
    };
  },
);

/* -------------------------------------------------------------------------------------------------
 * markThreadNotResolved
 * -------------------------------------------------------------------------------------------------
 */

export const markThreadNotResolved = withActionContext(
  async (
    context,
    environment,
    props: {
      threadId: string;
    },
  ) => {
    const ownerOrganizationId = environment.auth.getAndAssertCurrentUserOwnerOrganizationId();

    const [_originalThreadTag] = await context.environment.loader.getRecord(
      getPointer("thread_tag", {
        thread_id: props.threadId,
        tag_id: generateRecordId("singleton_tag", { name: "resolved" }),
      }),
    );

    return {
      forward: async () => {
        const action = await actions.thread.markThreadNotResolved.action(props, context);

        await write(environment, {
          action,
          onOptimisticWrite: () => {
            toast("vanilla", {
              subject: `Thread marked not resolved.`,
            });

            reindexNotification(environment, { threadId: props.threadId });
          },
        });
      },
      backward: async () => {
        const originalThreadTag = _originalThreadTag as ThreadResolvedThreadTagRecord | null;

        if (!originalThreadTag) {
          throw new InvalidActionStateError("[markThreadNotResolved] [backward] Cannot find original thread_tag");
        }

        const action = await actions.thread.markThreadResolved.action(
          {
            threadId: props.threadId,
            resolvedByMessageId: originalThreadTag.data.message_id,
            ownerOrganizationId,
          },
          context,
        );

        await write(environment, {
          action,
          onOptimisticWrite: () => {
            toast("vanilla", {
              subject: `Restored original thread resolution.`,
            });

            reindexNotification(environment, { threadId: props.threadId });
          },
        });
      },
    };
  },
);

/* -------------------------------------------------------------------------------------------------
 * deleteThread
 * -------------------------------------------------------------------------------------------------
 */

export const deleteThread = withActionContext(
  async (
    context,
    environment,
    props: {
      threadId: string;
    },
  ) => {
    return {
      forward: async () => {
        const action = await actions.thread.deleteThread.action(props, context);

        await write(environment, {
          action,
          onOptimisticWrite: () => {
            toast("vanilla", {
              subject: `Thread deleted.`,
            });
          },
        });
      },
      backward: async () => {
        const action = await actions.thread.restoreThread.action(props, context);

        await write(environment, {
          action,
          onOptimisticWrite: () => {
            toast("vanilla", {
              subject: `Thread restored.`,
            });
          },
        });
      },
    };
  },
);

/* -------------------------------------------------------------------------------------------------
 * restoreThread
 * -------------------------------------------------------------------------------------------------
 */

export const restoreThread = withActionContext(
  async (
    context,
    environment,
    props: {
      threadId: string;
    },
  ) => {
    return {
      forward: async () => {
        const action = await actions.thread.restoreThread.action(props, context);

        await write(environment, {
          action,
          onOptimisticWrite: () => {
            toast("vanilla", {
              subject: `Thread restored.`,
            });
          },
        });
      },
      backward: async () => {
        const action = await actions.thread.deleteThread.action(props, context);

        await write(environment, {
          action,
          onOptimisticWrite: () => {
            toast("vanilla", {
              subject: `Thread deleted.`,
            });
          },
        });
      },
    };
  },
);

/* -----------------------------------------------------------------------------------------------*/
